import { useMemo } from "react";
import { RadioTower, Wifi, MapPin, TrendingUp, Banknote, Zap, Users, Timer, Boxes } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolHeader } from "@/components/common/ToolHeader";
import { useCalculation } from "@/components/common/useCalculation";
import { ISP_DEFAULT, computeIsp } from "@/lib/engines/isp";
import { KpiCard } from "@/components/common/Cards";
import { fmtNum, fmtPct, fmtRp, fmtRpShort } from "@/lib/format";
import RegionPanel from "@/components/isp/RegionPanel";
import CapexPanel from "@/components/isp/CapexPanel";
import OpexPanel from "@/components/isp/OpexPanel";
import IspPackagesPanel from "@/components/isp/IspPackagesPanel";
import IspResultsPanel from "@/components/isp/IspResultsPanel";

export default function IspToolPage() {
  const calc = useCalculation("isp", ISP_DEFAULT, "Perhitungan ISP/FTTH");
  const { data, setData, readOnly } = calc;

  const res = useMemo(() => computeIsp(data), [data]);

  const update = (key) => (updater) =>
    setData((d) => ({ ...d, [key]: typeof updater === "function" ? updater(d[key]) : updater }));

  const summary = () => ({
    headline: `${data.region.name} · ${fmtNum(res.totalSubs)} subs · margin ${fmtPct(res.marginPct)}`,
    values: {
      subscribers: res.totalSubs,
      revenue: Math.round(res.revenue),
      cost: Math.round(res.totalCost),
      marginPct: Math.round(res.marginPct),
      breakEven: res.breakEvenSubs,
      payback: res.paybackMonths,
    },
  });

  const aiContext = () => ({
    region: data.region,
    capex: data.capex,
    opex: data.opex,
    bandwidth: data.bandwidth,
    subscriber: data.subscriber,
    fees: data.fees,
    allocation: data.allocation,
    packages: data.packages,
    projection: data.projection,
    hasil: {
      capexTotal: Math.round(res.capexTotal),
      capexMonthly: Math.round(res.capexMonthly),
      opexMonthly: Math.round(res.opexMonthly),
      bandwidthCost: Math.round(res.bandwidthCost),
      requiredMbps: Math.round(res.requiredMbps),
      cpePerSub: Math.round(res.cpePerSub),
      totalCost: Math.round(res.totalCost),
      revenue: Math.round(res.revenue),
      grossProfit: Math.round(res.grossProfit),
      marginPct: res.marginPct,
      breakEvenSubs: res.breakEvenSubs,
      paybackMonths: res.paybackMonths,
      cogsPerMbps: Math.round(res.cogsPerMbps),
      costPerSub: Math.round(res.costPerSub),
      packages: res.rows.map((r) => ({
        name: r.name,
        speed: r.speedMbps,
        price: r.price,
        cogs: Math.round(r.cogs),
        margin: Math.round(r.margin),
        subs: r.subs,
      })),
    },
  });

  return (
    <div className="max-w-[1600px] mx-auto">
      <ToolHeader
        icon={RadioTower}
        eyebrow="Tool 03 · ISP / FTTH"
        title="COGS Jaringan ISP"
        description="Hitung COGS paket internet per wilayah dari CAPEX (OLT, FO, ODP, perizinan) dan OPEX (SDM, listrik, DC, bandwidth upstream). Analisa break-even, payback, dan proyeksi bulanan."
        calc={calc}
        tool="isp"
        getSummary={summary}
        getAiContext={aiContext}
      />

      <div className="grid sm:grid-cols-2 xl:grid-cols-6 gap-3 mb-5">
        <KpiCard label="Pendapatan" value={fmtRpShort(res.revenue)} sub="per bulan" icon={Banknote} accent testid="isp-kpi-revenue" />
        <KpiCard label="Total biaya" value={fmtRpShort(res.totalCost)} sub="CAPEX amortisasi + OPEX + variabel" icon={Zap} testid="isp-kpi-cost" />
        <KpiCard
          label="Gross margin"
          value={fmtPct(res.marginPct)}
          sub={fmtRpShort(res.grossProfit)}
          tone={res.marginPct >= 25 ? "good" : res.marginPct >= 10 ? undefined : "bad"}
          icon={TrendingUp}
          testid="isp-kpi-margin"
        />
        <KpiCard label="Pelanggan" value={fmtNum(res.totalSubs)} sub={res.takeRate !== null ? `take-rate ${fmtPct(res.takeRate, 1)}` : "Jumlah pelanggan"} icon={Users} testid="isp-kpi-subs" />
        <KpiCard
          label="Break-even"
          value={res.breakEvenSubs ? `${fmtNum(res.breakEvenSubs)} subs` : "–"}
          sub={res.breakEvenSubs ? "Pelanggan minimum" : "Margin kontribusi ≤ 0"}
          icon={Timer}
          testid="isp-kpi-breakeven"
        />
        <KpiCard
          label="Payback"
          value={res.paybackMonths ? `${fmtNum(res.paybackMonths, 1)} bln` : "–"}
          sub={res.paybackMonths ? "Periode balik modal" : "EBITDA belum positif"}
          icon={Boxes}
          testid="isp-kpi-payback"
        />
      </div>

      <Tabs value={data.tab} onValueChange={(v) => setData((d) => ({ ...d, tab: v }))}>
        <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="isp-tabs">
          <TabsTrigger value="input" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="isp-tab-input">
            <MapPin className="h-4 w-4 mr-1.5" /> Wilayah
          </TabsTrigger>
          <TabsTrigger value="capex" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="isp-tab-capex">
            <Zap className="h-4 w-4 mr-1.5" /> CAPEX
          </TabsTrigger>
          <TabsTrigger value="opex" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="isp-tab-opex">
            <Banknote className="h-4 w-4 mr-1.5" /> OPEX & Bandwidth
          </TabsTrigger>
          <TabsTrigger value="packages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="isp-tab-packages">
            <Wifi className="h-4 w-4 mr-1.5" /> Paket
          </TabsTrigger>
          <TabsTrigger value="results" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="isp-tab-results">
            <TrendingUp className="h-4 w-4 mr-1.5" /> Hasil & Proyeksi
          </TabsTrigger>
        </TabsList>
        <TabsContent value="input" className="mt-5">
          <RegionPanel data={data} setData={setData} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="capex" className="mt-5">
          <CapexPanel capex={data.capex} setCapex={update("capex")} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="opex" className="mt-5">
          <OpexPanel data={data} setData={setData} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="packages" className="mt-5">
          <IspPackagesPanel data={data} setData={setData} setPackages={update("packages")} result={res} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="results" className="mt-5">
          <IspResultsPanel data={data} result={res} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
